/**
 * Invoice Uploader Component — comprobante de compra
 *
 * Jorge, 2026-07-28: "va a subir el comprobante al app — ya sea que lo
 * manden a un correo electrónico de ProMedic, o que tenga una factura
 * física: ésta se guarda y se le toma una foto con un celular".
 *
 * O sea que el camino PRINCIPAL es la cámara del teléfono, parado frente
 * al mostrador de la ferretería. De ahí las tres cosas que cambiaron:
 *
 *  1. BOTÓN DE CÁMARA. Antes había un solo input sin `capture`, así que
 *     en el celular abría el explorador de archivos: para usarlo había
 *     que salir de la app, abrir la cámara, sacar la foto, volver y
 *     buscarla. Ahora hay dos entradas — "Tomar foto"
 *     (`capture="environment"`, cámara trasera) y "Elegir archivo" (para
 *     el PDF que llegó por correo, y para desktop). Mismo patrón que
 *     `src/pages/technician/TechnicianTaskDetail.js:1015-1029`.
 *
 *  2. MOBILE-FIRST DE VERDAD. Los botones eran `size="sm"` (~31px de
 *     alto); el mínimo es 44px. Ahora son botones normales, apilados a
 *     ancho completo bajo 640px, y el texto de ayuda es ≥14px.
 *
 *  3. SE GUARDA EL PATH, NO LA URL FIRMADA. Ver la nota larga en
 *     `purchaseService.resolveAttachmentUrl`: la signed URL muere en una
 *     hora y dejaba el comprobante inalcanzable. El `value` de este
 *     componente es ahora el PATH del objeto; la vista previa se firma
 *     aparte y es descartable.
 *
 * @module components/purchases/InvoiceUploader
 */

import React, { useRef, useState, useEffect } from 'react';
import { Button, Spinner, Alert } from 'react-bootstrap';
import { uploadInvoice, resolveAttachmentUrl } from '../../services/purchaseService';
import { toast } from '../../utils/toast';
import './InvoiceUploader.css';

const ACCEPTED_TYPES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

/**
 * 5 MB — es el límite del bucket `receipts`
 * (supabase/migrations/00000000000001_baseline_storage.sql:64-69), NO una
 * preferencia nuestra. Storage rechaza cualquier cosa mayor ANTES de que
 * corra RLS, así que anunciar 10 MB acá (como decía antes) solo servía
 * para que el usuario perdiera la subida después de esperarla.
 */
const MAX_SIZE = 5 * 1024 * 1024;

/**
 * @param {Object} props
 * @param {string} [props.value] - Path del comprobante en el bucket `receipts`
 *   (o una URL legacy, en filas anteriores a este cambio).
 * @param {Function} props.onChange - (path|null) => void
 * @param {string} [props.purchaseId] - Optional purchase id for storage path
 * @param {boolean} [props.disabled=false]
 * @param {boolean} [props.required=false] - Pinta el estado "falta el comprobante".
 */
const InvoiceUploader = ({ value, onChange, purchaseId, disabled = false, required = false }) => {
  const cameraInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [viewUrl, setViewUrl] = useState(null);

  // La URL para "Ver comprobante" se firma fresca cada vez que cambia el
  // path guardado. Nunca se persiste.
  useEffect(() => {
    let cancelled = false;
    if (!value) {
      setViewUrl(null);
      return undefined;
    }
    resolveAttachmentUrl(value).then((url) => {
      if (!cancelled) setViewUrl(url);
    });
    return () => { cancelled = true; };
  }, [value]);

  const handlePick = (ref) => {
    if (disabled || uploading) return;
    ref.current?.click();
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError(null);

    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError('Formato no soportado. Usá una foto (JPG, PNG) o un PDF.');
      e.target.value = '';
      return;
    }
    if (file.size > MAX_SIZE) {
      setError('El archivo pesa más de 5 MB. Si es una foto, tomala de nuevo con menos resolución.');
      e.target.value = '';
      return;
    }

    setUploading(true);
    try {
      const { data, error: upErr } = await uploadInvoice(file, purchaseId);
      if (upErr) throw upErr;
      // Se sube el PATH, no la URL firmada.
      onChange && onChange(data?.path || null);
      if (data?.previewUrl) setViewUrl(data.previewUrl);
      toast.success('Comprobante cargado');
    } catch (err) {
      console.error('Invoice upload failed:', err);
      setError(err.message || 'Error al subir el comprobante.');
      toast.error('Error al subir el comprobante');
    } finally {
      setUploading(false);
      if (e.target) e.target.value = '';
    }
  };

  const handleRemove = () => {
    if (disabled) return;
    onChange && onChange(null);
    setViewUrl(null);
  };

  const busy = disabled || uploading;

  return (
    <div className="invoice-uploader">
      {/* Cámara trasera del teléfono — el camino principal */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFile}
        className="invoice-uploader__input"
        disabled={busy}
        aria-hidden="true"
        tabIndex={-1}
      />
      {/* Archivo ya existente: el PDF que llegó por correo, o desktop */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,.webp"
        onChange={handleFile}
        className="invoice-uploader__input"
        disabled={busy}
        aria-hidden="true"
        tabIndex={-1}
      />

      {value ? (
        <div className="invoice-uploader__actions">
          <a
            href={viewUrl || undefined}
            target="_blank"
            rel="noopener noreferrer"
            className={`btn btn-outline-primary invoice-uploader__btn${viewUrl ? '' : ' disabled'}`}
            aria-disabled={!viewUrl}
          >
            <i className="bi bi-file-earmark-text me-2" aria-hidden="true"></i>
            {viewUrl ? 'Ver comprobante' : 'Abriendo…'}
          </a>
          <Button
            variant="outline-secondary"
            className="invoice-uploader__btn"
            onClick={() => handlePick(cameraInputRef)}
            disabled={busy}
          >
            {uploading ? (
              <><Spinner animation="border" size="sm" className="me-2" />Subiendo…</>
            ) : (
              <><i className="bi bi-camera me-2" aria-hidden="true"></i>Volver a tomar</>
            )}
          </Button>
          <Button
            variant="outline-danger"
            className="invoice-uploader__btn"
            onClick={handleRemove}
            disabled={busy}
          >
            <i className="bi bi-x-lg me-2" aria-hidden="true"></i>
            Quitar
          </Button>
        </div>
      ) : (
        <div className="invoice-uploader__actions">
          <Button
            variant="primary"
            className="invoice-uploader__btn"
            onClick={() => handlePick(cameraInputRef)}
            disabled={busy}
          >
            {uploading ? (
              <><Spinner animation="border" size="sm" className="me-2" />Subiendo…</>
            ) : (
              <><i className="bi bi-camera-fill me-2" aria-hidden="true"></i>Tomar foto de la factura</>
            )}
          </Button>
          <Button
            variant="outline-primary"
            className="invoice-uploader__btn"
            onClick={() => handlePick(fileInputRef)}
            disabled={busy}
          >
            <i className="bi bi-paperclip me-2" aria-hidden="true"></i>
            Elegir archivo (PDF o foto)
          </Button>
        </div>
      )}

      {!value && required && !error && (
        <p className="invoice-uploader__hint invoice-uploader__hint--required">
          <i className="bi bi-exclamation-circle me-1" aria-hidden="true"></i>
          El comprobante es obligatorio para registrar la factura.
        </p>
      )}
      {!value && !required && !error && (
        <p className="invoice-uploader__hint">
          Si tenés la factura en papel, tomale una foto. Si llegó por correo, subí el PDF.
        </p>
      )}

      {error && (
        <Alert variant="danger" className="invoice-uploader__error mb-0 mt-2">
          <i className="bi bi-exclamation-triangle me-2" aria-hidden="true"></i>
          {error}
        </Alert>
      )}
    </div>
  );
};

export default InvoiceUploader;
